import Vuex from "vuex";
import Vue from "vue";
import VuexPersistence from "vuex-persist";
import Cookies from "js-cookie";

// cookie storage
const cookieStorage = {
  getItem: key => Cookies.get(key),
  setItem: (key, value) =>
    Cookies.set(key, value, {
      secure: false,
      sameSite: "Strict",
    }),
  removeItem: key => Cookies.remove(key),
};

// globální (cookies)
const vuexCookie = new VuexPersistence({
  key: "powebStoreCookie",
  storage: cookieStorage,
  reducer: state => ({
    email: state.email,
    cartCount: state.cartCount,
    cartSum: state.cartSum,
    cartContent: state.cartContent,
    cartAmountOverrides: state.cartAmountOverrides,
  }),
});

// lokální (jen tab) - data budou sdílena mezi taby, ale ne mezi okny prohlížeče a budou vymazána po zavření všech oken/tabů
const vuexSession = new VuexPersistence({
  key: "powebStoreSession",
  storage: window.sessionStorage,
  reducer: state => ({
    organizace: state.organizace,
    detailId: state.detailId,
    formularId: state.formularId,
    formularNazev: state.formularNazev,
    informaceOPodani: state.informaceOPodani,
    informaceNadpis: state.informaceNadpis,
    kategorieFormularu: state.kategorieFormularu,
    dlazdiceUFormularu: state.dlazdiceUFormularu,
    detailPlatbaId: state.detailPlatbaId,
    stavZadostiId: state.stavZadostiId,
    lastVisitedPage: state.lastVisitedPage,
    htmlError: "",
    formData: state.formData,
  }),
});

Vue.use(Vuex);

const getDefaultState = () => {
  return {
    // cookie storage
    email: null,
    cartCount: 0,
    cartSum: 0,
    cartContent: [],
    cartAmountOverrides: {},

    // session storage
    organizace: [],
    detailId: null,
    formularId: null,
    formularNazev: null,
    kategorieFormularu: null,
    dlazdiceUFormularu: null,
    detailPlatbaId: null,
    stavZadostiId: null,
    lastVisitedPage: null,
    htmlError: "",
    formData: {},

    // local state
    globalLoading: false,
    isHomeSelected: false,
    isPoplatkySelected: false,
    isFormulareSelected: false,
    isStavyZadostiSelected: false,
    isUhrazeneSelected: false,
    isContactsSelected: false,
    isConditionsSelected: false,
    isProtectionSelected: false,
    isProhlaseniOPristupnostiSelected: false,
    isCartSelected: false,
    isQrPaymentSelected: false,

    // TODO: nemelo by se pamatovat, kdyz ze stranky vypadnu a pak dam zpet
    blobPodani: null,
    blobOsvedceni: null,
    detailHrazenyPoplatek: null,
    logo: null,
    // TODO: mozna pridat i udaje formulare, aby kdyz se vratim na formular, tak abych mel vyplnene udaje

    // platbu neukládat nikam, obsahuje osobní údaje v účelu platby
    payment: null,
  };
};

// helpers pro reset
function resetGlobalState(_state) {
  _state.email = null
  _state.cartCount = 0
  _state.cartSum = 0
  _state.cartContent = []
  _state.cartAmountOverrides = {}
  _state.lastVisitedPage = null
}

function resetSessionState(_state) {
  _state.organizace = []
  _state.detailId = null
  _state.detailPlatbaId = null
  _state.stavZadostiId = null
  _state.formularId = null
  _state.formularNazev = null
  _state.informaceOPodani = null
  _state.informaceNadpis = null
  _state.kategorieFormularu = null
  _state.dlazdiceUFormularu = null
  _state.htmlError = ""
  _state.formData = {}
}

function resetLocalState(_state) {
  _state.globalLoading = false
  _state.isHomeSelected = false
  _state.isPoplatkySelected = false
  _state.isFormulareSelected = false
  _state.isStavyZadostiSelected = false
  _state.isUhrazeneSelected = false
  _state.isContactsSelected = false
  _state.isConditionsSelected = false
  _state.isProtectionSelected = false
  _state.isProhlaseniOPristupnostiSelected = false
  _state.isCartSelected = false
  _state.isQrPaymentSelected = false

  _state.blobPodani = null
  _state.blobOsvedceni = null
  _state.detailHrazenyPoplatek = null
  _state.logo = null
  _state.payment = null
}

export const store = new Vuex.Store({
  state: getDefaultState(),

  mutations: {
    // Reset pri logoutu (volat reset vseho)
    RESET_globalState: resetGlobalState,
    // Reset pri loginu (nove prihlaseni nanecisto, volat i s resetem local state)
    RESET_sessionState: resetSessionState,
    // Reset docasych udaju
    RESET_localState: resetLocalState,
    // Reset vseho
    RESET_state: _state => {
      resetGlobalState(_state)
      resetSessionState(_state)
      resetLocalState(_state)
    },

    // global loading
    SET_globalLoading: (_state, globalLoading) => {
      _state.globalLoading = globalLoading;
    },

    // cartContent
    SET_cartContent: (_state, cartContent) => {
      _state.cartContent = cartContent;
    },
    ADD_cartContent: (_state, id) => {
      _state.cartContent.push(id);
    },
    REMOVE_cartContent: (_state, id) => {
      var index = _state.cartContent.indexOf(id);
      _state.cartContent.splice(index, 1);
    },
    SET_cartAmountOverride: (_state, { id, castka }) => {
      Vue.set(_state.cartAmountOverrides, id, Number(castka));
    },
    REMOVE_cartAmountOverride: (_state, id) => {
      Vue.delete(_state.cartAmountOverrides, id);
    },
    SET_cartAmountOverrides: (_state, overrides) => {
      _state.cartAmountOverrides = overrides;
    },

    // cartCount
    SET_cartCount: (_state, cartCount) => {
      _state.cartCount = cartCount;
    },
    INCREMENT_cartCount: _state => {
      _state.cartCount += 1;
    },
    DECREMENT_cartCount: _state => {
      _state.cartCount -= 1;
    },

    // cartSum
    SET_cartSum: (_state, cartSum) => {
      _state.cartSum = cartSum;
    },
    ADD_cartSum: (_state, sum) => {
      _state.cartSum = _state.cartSum + sum;
    },
    SUB_cartSum: (_state, sum) => {
      _state.cartSum = _state.cartSum - sum;
    },

    // detailId
    SET_detailId: (_state, id) => {
      _state.detailId = id;
    },

    // detail platby
    SET_detailPlatbaId: (_state, platbaId) => {
      _state.detailPlatbaId = platbaId;
    },

   SET_stavZadostiId: (_state, stavZadostiId) => {
      _state.stavZadostiId = stavZadostiId;
    },

    // detail formulare
    SET_formularId: (_state, formularId) => {
      _state.formularId = formularId;
    },

    SET_formularNazev: (_state, formularNazev) => {
      _state.formularNazev = formularNazev;
    },

    SET_informaceOPodani: (_state, informaceOPodani) => {
      _state.informaceOPodani = informaceOPodani;
    },

    SET_informaceNadpis: (_state, informaceNadpis) => {
      _state.informaceNadpis = informaceNadpis;
    },

    // detail formulare
    SET_kategorieFormularu: (_state, kategorieFormularu) => {
      _state.kategorieFormularu = kategorieFormularu;
    },

    SET_dlazdiceUFormularu: (_state, dlazdiceUFormularu) => {
      _state.dlazdiceUFormularu = dlazdiceUFormularu;
    },

    // osvedceni zadosti
    SET_blobPodani: (_state, blobPodani) => {
      _state.blobPodani = blobPodani;
    },

    // osvedceni zadosti
    SET_blobOsvedceni: (_state, blobOsvedceni) => {
      _state.blobOsvedceni = blobOsvedceni;
    },

    // detail hrazeneho poplatku
    SET_detailHrazenyPoplatek: (_state, poplatek) => {
      _state.detailHrazenyPoplatek = poplatek;
    },

    // email
    SET_email: (_state, email) => {
      _state.email = email;
    },

    // oganizace
    SET_organizace: (_state, organizace) => {
      _state.organizace = organizace;
    },

    // payment
    SET_payment: (_state, payment) => {
      _state.payment = payment;
    },

    // menu active items
    SET_isHomeSelected: (_state, isSelected) => {
      _state.isHomeSelected = isSelected;
    },
    SET_isPoplatkySelected: (_state, isSelected) => {
      _state.isPoplatkySelected = isSelected;
    },
    SET_isFormulareSelected: (_state, isSelected) => {
      _state.isFormulareSelected = isSelected;
    },
    SET_isStavyZadostiSelected: (_state, isSelected) => {
      _state.isStavyZadostiSelected = isSelected;
    },
    SET_isUhrazeneSelected: (_state, isSelected) => {
      _state.isUhrazeneSelected = isSelected;
    },
    SET_isContactsSelected: (_state, isSelected) => {
      _state.isContactsSelected = isSelected;
    },
    SET_isConditionsSelected: (_state, isSelected) => {
      _state.isConditionsSelected = isSelected;
    },
    SET_isProtectionSelected: (_state, isSelected) => {
      _state.isProtectionSelected = isSelected;
    },
    SET_isProhlaseniOPristupnostiSelected: (_state, isSelected) => {
      _state.isProhlaseniOPristupnostiSelected = isSelected;
    },


    SET_isCartSelected: (_state, isSelected) => {
      _state.isCartSelected = isSelected;
    },
    SET_isQrPaymentSelected: (_state, isSelected) => {
      _state.isQrPaymentSelected = isSelected;
    },
    // html request error
    SET_htmlError: (_state, error) => {
      _state.htmlError = error;
    },
    // last visited page
    SET_lastVisitedPage: (_state, page) => {
      _state.lastVisitedPage = page;
    },
    // form data
    SET_formData: (_state, formData) => {
      _state.formData = formData;
    },
    UPDATE_formData: (_state, { fieldId, value }) => {
      _state.formData = { ..._state.formData, [fieldId]: value };
    },
  },
  actions: {
    reset({ commit }) {
      commit("RESET_state");
    },
    resetGlobalState({ commit }) {
      commit("RESET_globalState");
    },
    resetSessionState({ commit }) {
      commit("RESET_sessionState");
    },
    resetLocalState({ commit }) {
      commit("RESET_localState");
    },
    resetAndSetGlobalLoadingToTrue({ commit }) {
      commit("RESET_state");
      commit("SET_globalLoading", true);
    },
    setGlobalLoading({ commit }, globalLoading) {
      commit("SET_globalLoading", globalLoading);
    },
    setDetail({ commit }, poplatek) {
      commit("SET_detailId", poplatek.id);
    },
    setDetailPlatbaId({ commit }, platbaId) {
      commit("SET_detailPlatbaId", platbaId);
    },
    setStavZadostiId({ commit }, stavZadostiId) {
      commit("SET_stavZadostiId", stavZadostiId);
    },
    setFormularId({ commit }, formularId) {
      commit("SET_formularId", formularId);
    },
    setFormularNazev({ commit }, formularNazev) {
      commit("SET_formularNazev", formularNazev);
    },
    setInformaceOPodani({ commit }, informaceOPodani) {
      commit("SET_informaceOPodani", informaceOPodani);
    },
    setInformaceNadpis({ commit }, informaceNadpis) {
      commit("SET_informaceNadpis", informaceNadpis);
    },
    setKategorieFormularu({ commit }, kategorieFormularu) {
      commit("SET_kategorieFormularu", kategorieFormularu);
    },
    setDlazdiceUFormularu({ commit }, dlazdiceUFormularu) {
      commit("SET_dlazdiceUFormularu", dlazdiceUFormularu);
    },
    setDetailHrazenyPoplatek({ commit }, poplatek) {
      commit("SET_detailHrazenyPoplatek", poplatek);
    },
    setEmail({ commit }, email) {
      commit("SET_email", email);
    },
    setBlobPodani({ commit }, blobPodani) {
      commit("SET_blobPodani", blobPodani);
    },
    setBlobOsvedceni({ commit }, blobOsvedceni) {
      commit("SET_blobOsvedceni", blobOsvedceni);
    },
    setOrganizace({ commit }, organizace) {
      commit("SET_organizace", organizace);
    },
    setPayment({ commit }, payment) {
      commit("SET_payment", payment);
    },
    addToCart({ commit }, poplatek) {
      const castka = Number(poplatek.castka);
      commit("ADD_cartContent", poplatek.id);
      commit("SET_cartAmountOverride", { id: poplatek.id, castka });
      commit("INCREMENT_cartCount");
      commit("ADD_cartSum", castka);
    },
    removeFromCart({ commit, state }, poplatek) {
      const castka = state.cartAmountOverrides[poplatek.id] !== undefined
        ? Number(state.cartAmountOverrides[poplatek.id])
        : Number(poplatek.castka);
      commit("REMOVE_cartContent", poplatek.id);
      commit("REMOVE_cartAmountOverride", poplatek.id);
      commit("DECREMENT_cartCount");
      commit("SUB_cartSum", castka);
    },
    setCartContent({ commit, state }, poplatky) {
      commit("SET_cartCount", poplatky.length);
      var cartSum = 0;
      var ids = [];
      var syncedOverrides = {};
      poplatky.forEach(poplatek => {
        const castka = state.cartAmountOverrides[poplatek.id] !== undefined
          ? Number(state.cartAmountOverrides[poplatek.id])
          : Number(poplatek.castka);
        cartSum += castka;
        ids.push(poplatek.id);
        syncedOverrides[poplatek.id] = castka;
      });
      commit("SET_cartSum", cartSum);
      commit("SET_cartContent", ids);
      commit("SET_cartAmountOverrides", syncedOverrides);
    },

    unselectMenuItems({ commit }) {
      commit("SET_isContactsSelected", false);
      commit("SET_isConditionsSelected", false);
      commit("SET_isProtectionSelected", false);
      commit("SET_isProhlaseniOPristupnostiSelected", false);
      commit("SET_isHomeSelected", false);
      commit("SET_isFormulareSelected", false);
      commit("SET_isStavyZadostiSelected", false);
      commit("SET_isPoplatkySelected", false);
      commit("SET_isUhrazeneSelected", false);
      commit("SET_isCartSelected", false);
      commit("SET_isQrPaymentSelected", false);
    },

    selectHomeMenuItem({ commit, dispatch }) {
      dispatch("unselectMenuItems");
      commit("SET_isHomeSelected", true);
    },
    selectPoplatkyMenuItem({ commit, dispatch }) {
      dispatch("unselectMenuItems");
      commit("SET_isPoplatkySelected", true);
    },
    selectFormulareMenuItem({ commit, dispatch }) {
      dispatch("unselectMenuItems");
      commit("SET_isFormulareSelected", true);
    },
    selectStavyZadostiMenuItem({ commit, dispatch }) {
      dispatch("unselectMenuItems");
      commit("SET_isStavyZadostiSelected", true);
    },
    selectUhrazeneMenuItem({ commit, dispatch }) {
      dispatch("unselectMenuItems");
      commit("SET_isUhrazeneSelected", true);
    },
    selectContactsMenuItem({ commit, dispatch }) {
      dispatch("unselectMenuItems");
      commit("SET_isContactsSelected", true);
    },
    selectConditionsMenuItem({ commit, dispatch }) {
      dispatch("unselectMenuItems");
      commit("SET_isConditionsSelected", true);
    },
    selectProtectionMenuItem({ commit, dispatch }) {
      dispatch("unselectMenuItems");
      commit("SET_isProtectionSelected", true);
    },
    selectProhlaseniOPristupnostiMenuItem({ commit, dispatch }) {
      dispatch("unselectMenuItems");
      commit("SET_isProhlaseniOPristupnostiSelected", true);
    },
    selectCart({ commit, dispatch }) {
      dispatch("unselectMenuItems");
      commit("SET_isCartSelected", true);
    },
    selectQrPayment({ commit, dispatch }) {
      dispatch("unselectMenuItems");
      commit("SET_isQrPaymentSelected", true);
    },
    emptyCart({ commit }) {
      commit("SET_cartContent", []);
      commit("SET_cartCount", 0);
      commit("SET_cartSum", 0);
      commit("SET_cartAmountOverrides", {});
    },
    setHtmlError({ commit }, error) {
      commit("SET_htmlError", error);
    },
    setLastVisitedPage({ commit }, page) {
      commit("SET_lastVisitedPage", page);
    },
    setFormData({ commit }, formData) {
      commit("SET_formData", formData);
    },
    updateFormData({ commit }, payload) {
      commit("UPDATE_formData", payload);
    },
  },
  getters: {

    // z předaných poplatků odfiltruje ty které již jsou v košíku
    filterByCartContent: _state => poplatky => {
      return poplatky.filter(function(e) {
        for (var i = 0; i < _state.cartContent.length; i++) {
          if (e.id == _state.cartContent[i]) {
            return false;
          }
        }
        return true;
      });
    },
    // z předaných poplatků odfiltruje ty které již nejsou v košíku
    filterByCartContentExclude: _state => poplatky => {
      return poplatky.filter(function(e) {
        for (var i = 0; i < _state.cartContent.length; i++) {
          if (e.id == _state.cartContent[i]) {
            return true;
          }
        }
        return false;
      });
    },

    isInCart: _state => id => {
      for (var i = 0; i < _state.cartContent.length; i++) {
        if (id == _state.cartContent[i]) {
          return true;
        }
      }
      return false;
    },

    isCartEmpty: _state => () => {
      return _state.cartContent.length == 0;
    },
    getCartAmountOverride: _state => id => {
      return _state.cartAmountOverrides[id];
    },
  },
  plugins: [vuexCookie.plugin, vuexSession.plugin],
});
